import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Target, TrendingUp, TrendingDown, Minus, DollarSign, Scissors, Package } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) {
  return '$' + Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function Bar({ pct }) {
  const value = Math.min(Math.max(pct ?? 0, 0), 100);
  const color = value >= 100 ? 'bg-green-500' : value >= 70 ? 'bg-gold' : value >= 40 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="w-full bg-zinc-800 rounded-full h-1.5 mt-1.5">
      <div className={`${color} h-1.5 rounded-full transition-all`} style={{ width: `${value}%` }} />
    </div>
  );
}

function Delta({ value, formatter = (v) => v, suffix = '' }) {
  if (value === 0 || value == null) return <span className="text-zinc-500 flex items-center gap-0.5"><Minus className="w-3 h-3" />sin cambio</span>;
  const up = value > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const color = up ? 'text-green-400' : 'text-red-400';
  return (
    <span className={`${color} flex items-center gap-0.5`}>
      <Icon className="w-3 h-3" />
      {up ? '+' : ''}{formatter(value)}{suffix}
    </span>
  );
}

function GoalRow({ icon: Icon, label, current, goal, pct, deltaValue, deltaFormatter, currentFormatter, suffix = '', testId }) {
  const hasGoal = goal != null && goal > 0;
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5 text-zinc-400" />
          <span className="text-xs text-zinc-300">{label}</span>
        </div>
        <div className="text-right">
          <p className="text-sm text-white">
            <span className="font-semibold">{currentFormatter ? currentFormatter(current) : current}{suffix}</span>
            {hasGoal && (
              <span className="text-zinc-500 text-[11px]"> / {currentFormatter ? currentFormatter(goal) : goal}{suffix}</span>
            )}
          </p>
          <div className="text-[10px] mt-0.5 flex items-center gap-2 justify-end">
            {hasGoal && (
              <span className={pct >= 100 ? 'text-green-400 font-semibold' : pct >= 70 ? 'text-gold' : 'text-zinc-500'} data-testid={`${testId}-pct`}>
                {pct}%
              </span>
            )}
            <Delta value={deltaValue} formatter={deltaFormatter || ((v) => v)} suffix={suffix} />
          </div>
        </div>
      </div>
      {hasGoal && <Bar pct={pct} />}
    </div>
  );
}

export default function GoalProgressCard({ barberId, getAuthHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!barberId) return;
    setLoading(true);
    axios.get(`${API}/barbers/${barberId}/goal-progress`, getAuthHeaders())
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [barberId, getAuthHeaders]);

  if (loading) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800 p-5">
        <p className="text-zinc-500 text-sm text-center">Cargando metas...</p>
      </Card>
    );
  }

  if (!data) return null;

  const { goals, current, progress_pct, deltas_vs_last_month } = data;
  const hasAnyGoal = (goals.avg_ticket > 0) || (goals.services > 0) || (goals.products_revenue > 0) || (goals.extra_services > 0) || (goals.neurone_revenue > 0);

  return (
    <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="goal-progress-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm text-zinc-400 flex items-center gap-2">
          <Target className="w-4 h-4 text-gold" />
          Metas Personales del Mes
        </h3>
        <span className="text-[10px] text-zinc-600">vs mes pasado</span>
      </div>
      {!hasAnyGoal ? (
        <p className="text-zinc-500 text-xs text-center py-3">
          Aún no tienes metas asignadas. Pídele al Master Admin que las configure en tu perfil.
        </p>
      ) : (
        <div className="space-y-3.5">
          <GoalRow
            icon={DollarSign}
            label="Ticket Promedio"
            current={current.avg_ticket}
            goal={goals.avg_ticket}
            pct={progress_pct.avg_ticket}
            deltaValue={deltas_vs_last_month.avg_ticket}
            currentFormatter={fmt}
            deltaFormatter={fmt}
            testId="goal-avg-ticket"
          />
          <GoalRow
            icon={Scissors}
            label="Servicios Realizados"
            current={current.services_count}
            goal={goals.services}
            pct={progress_pct.services}
            deltaValue={deltas_vs_last_month.services}
            testId="goal-services"
          />
          <GoalRow
            icon={Package}
            label="Venta de Producto"
            current={current.products_revenue}
            goal={goals.products_revenue}
            pct={progress_pct.products_revenue}
            deltaValue={deltas_vs_last_month.products_revenue}
            currentFormatter={fmt}
            deltaFormatter={fmt}
            testId="goal-products"
          />
          {goals.extra_services > 0 && (
            <GoalRow
              icon={Scissors}
              label="Servicios EXTRA"
              current={current.extra_services_count}
              goal={goals.extra_services}
              pct={progress_pct.extra_services}
              deltaValue={deltas_vs_last_month.extra_services}
              testId="goal-extra-services"
            />
          )}
          {goals.neurone_revenue > 0 && (
            <GoalRow
              icon={Package}
              label="Productos NEURONE"
              current={current.neurone_revenue}
              goal={goals.neurone_revenue}
              pct={progress_pct.neurone_revenue}
              deltaValue={deltas_vs_last_month.neurone_revenue}
              currentFormatter={fmt}
              deltaFormatter={fmt}
              testId="goal-neurone-revenue"
            />
          )}
        </div>
      )}
    </Card>
  );
}
